import React, { useEffect, useMemo, useState } from 'react';
import { camelizeKeys } from '../../../../../utils/camelizeKeys';
import { ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import Title from './components/Title';
import { formatCurrency, formatPercentage } from '../../../../../utils/formatters';
import SortableHeader from '../../../../../components/ui/table/SortableHeader';
import CustomCheckbox from '../../../../../components/ui/checkboxes/CustomCheckbox';
import BalancingBadge from '../../../../../components/ui/badges/BalancingBadge';
import SalesPlanning from './equities/SalesPlanning';
import PurchasePlanning from './equities/PurchasePlanning';
import Info from './investmentFunds/Info';
import { useToast } from '../../../../../components/ui/toast/ToastContext';

const GRID = '30px 100px 70px 1fr 1fr 1fr 1fr 1.5fr 100px';

const normalizeSortableText = (value) => {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim();
};

const getSortableValue = (position, field) => {
  switch (field) {
    case 'ticker':
      return normalizeSortableText(position.product && position.product.ticker);

    case 'quantity':
      return Number(position.quantity) || 0;

    case 'averagePrice':
      return Number(position.averagePrice) || 0;

    case 'marketPrice':
      return Number(position.product && position.product.marketPrice) || 0;

    case 'amount':
      return Number(position.amount) || 0;

    case 'idealAmount':
      return Number(position.idealAmount) || 0;

    case 'profit':
      return Number(position.profit) || 0;

    case 'balancingQuantity':
      return Number(position.balancingQuantity) || 0;

    default: {
      const value = position[field];

      if (typeof value === 'number') {
        return value;
      }

      return normalizeSortableText(value);
    }
  }
};

const sortEquityPositions = (positions = [], sortBy, sortOrder) => {
  const direction = sortOrder === 'desc' ? -1 : 1;

  return [...positions]
    .map((position, index) => ({ position, index }))
    .sort((a, b) => {
      const valueA = getSortableValue(a.position, sortBy);
      const valueB = getSortableValue(b.position, sortBy);

      let result = 0;

      if (typeof valueA === 'number' && typeof valueB === 'number') {
        result = valueA - valueB;
      } else {
        result = String(valueA).localeCompare(String(valueB), 'pt-BR', {
          numeric: true,
          sensitivity: 'base',
        });
      }

      if (result === 0) {
        return a.index - b.index;
      }

      return result * direction;
    })
    .map(({ position }) => position);
};

const EquityRow = ({ position, idx, isSelected, onToggle }) => {
  const isBlueRow = idx % 2 === 0;

  const situationColor = (situation) => {
    switch (situation) {
      case 'wc':
        return 'text-greenBright';
      case 'w':
        return 'text-purpleBright';
      case 'c':
        return 'text-orangeBright';
      default:
        return '';
    }
  };

  const profitTextColor = (value) => {
    if (value > 0) return 'text-greenBright';
    if (value < 0) return 'text-redDeep';
    return '';
  };

  return (
    <div className={`${isBlueRow ? 'bg-blueLight' : 'bg-white'} rounded-xl`}>
      <div className="grid items-center gap-4 px-3 py-5 text-sm text-blueDarker" style={{ gridTemplateColumns: GRID }}>
        <CustomCheckbox checked={isSelected} onChange={() => onToggle(position.id)} />

        <div className={`truncate font-semibold ${situationColor(position.situation)}`} title={position.product.ticker}>
          {position.product.ticker}
        </div>

        <div className="truncate" title={position.quantity}>
          {parseInt(position.quantity)}
        </div>

        <div className="truncate" title={position.averagePrice}>
          {formatCurrency(position.averagePrice)}
        </div>

        <div className="truncate" title={position.product.marketPrice}>
          {position.product.marketPrice != null ? formatCurrency(position.product.marketPrice) : 'Preço indisponível'}
        </div>

        <div className="flex gap-2 flex-row items-center overflow-visible min-w-0" title={position.amount}>
          <span className="truncate">{formatCurrency(position.amount)}</span>
        </div>

        <div className="truncate" title={position.idealAmount}>
          {formatCurrency(position.idealAmount)}
        </div>

        <div className={`truncate ${profitTextColor(position.profit)} font-semibold`} title={position.profit}>
          {formatCurrency(position.profit)} <span>({formatPercentage(position.profitPercentage)})</span>
        </div>

        <div className="truncate">
          {position.balancingQuantity != null ? (
            <BalancingBadge
              value={position.balancingQuantity}
              positiveColors="bg-tealBright text-tealDark"
              valueType="int"
            />
          ) : (
            <span className="text-xs text-blueSteel">Preço indisponível</span>
          )}
        </div>
      </div>
    </div>
  );
};

const EquityAccordion = ({
  title,
  investedPercentage,
  positions,
  selectedIds,
  onToggle,
  onToggleAll,
  defaultOpen = true,
}) => {
  const [isOpen, setIsOpen] = useState(defaultOpen);
  const [sortBy, setSortBy] = useState('ticker');
  const [sortOrder, setSortOrder] = useState('asc');

  const sortedPositions = useMemo(() => {
    return sortEquityPositions(positions || [], sortBy, sortOrder);
  }, [positions, sortBy, sortOrder]);

  const visibleIds = positions.map((p) => p.id);
  const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.includes(id));
  const someSelected = visibleIds.some((id) => selectedIds.includes(id)) && !allSelected;

  const handleSort = (field) => {
    if (sortBy === field) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
  };

  return (
    <div className="flex flex-col gap-2">
      <div
        className="flex items-center justify-between bg-tealMedium text-white font-semibold px-4 py-3 rounded-xl cursor-pointer select-none hover:bg-tealDark transition-colors shadow-sm"
        onClick={() => setIsOpen((v) => !v)}
      >
        <span className="text-base">{title}</span>

        <div className="flex items-center gap-3">
          <span className="text-sm font-medium opacity-90">
            Investido: <span className="font-bold">{formatPercentage(investedPercentage)}</span>
          </span>

          {isOpen ? <ChevronUp size={18} /> : <ChevronDown size={18} />}
        </div>
      </div>

      <div
        className={`grid transition-all duration-300 ease-in-out ${isOpen ? 'opacity-100' : 'opacity-0'}`}
        style={{ gridTemplateRows: isOpen ? '1fr' : '0fr' }}
      >
        <div className="overflow-hidden">
          <div className="px-2 pt-1 pb-2">
            <div
              className="grid items-center gap-4 text-blueDarker text-sm font-semibold px-3 py-2"
              style={{ gridTemplateColumns: GRID }}
            >
              <CustomCheckbox
                checked={allSelected}
                indeterminate={someSelected}
                onChange={() => onToggleAll(visibleIds)}
              />

              <SortableHeader field="ticker" label="Ticker" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader field="quantity" label="Qtd" sortBy={sortBy} sortOrder={sortOrder} onSort={handleSort} />

              <SortableHeader
                field="averagePrice"
                label="Pr Médio"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="marketPrice"
                label="Pr Mercado"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="amount"
                label="Saldo Bruto"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="idealAmount"
                label="Saldo Ideal"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="profit"
                label="Lucro/Prejuízo"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />

              <SortableHeader
                field="balancingQuantity"
                label="Balanço"
                sortBy={sortBy}
                sortOrder={sortOrder}
                onSort={handleSort}
              />
            </div>

            <div className="space-y-0 mt-1">
              {sortedPositions.map((position, idx) => (
                <EquityRow
                  key={position.id}
                  position={position}
                  idx={idx}
                  isSelected={selectedIds.includes(position.id)}
                  onToggle={onToggle}
                />
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

const Equities = ({ apiClientId, accountNumber, clientName }) => {
  const { addToast } = useToast();

  const [data, setData] = useState({
    equityPositions: [],
    equityPositionsCount: 0,
    totalInEquities: 0,
    idealInEquities: 0,
    totalInEquitiesPercentage: 0,
    idealInEquitiesPercentage: 0,
    pendingSettlements: 0,
    kinds: [],
  });

  const [selectedIds, setSelectedIds] = useState([]);

  const toggleOne = (id) => {
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  };

  const toggleGroup = (ids) => {
    const allIn = ids.every((id) => selectedIds.includes(id));

    if (allIn) {
      setSelectedIds((prev) => prev.filter((id) => !ids.includes(id)));
    } else {
      setSelectedIds((prev) => [...new Set([...prev, ...ids])]);
    }
  };

  const fetchData = () => {
    fetch(`/app/analyst/client/${apiClientId}/dashboard/equities`)
      .then((res) => res.json())
      .then((responseData) => {
        const parsedData = camelizeKeys(responseData) || {};

        setData(parsedData);
        setSelectedIds(parsedData.equityPositions.map((position) => position.id));
      })
      .catch((err) => {
        console.error('Erro ao carregar dados', err);

        addToast({
          type: 'error',
          message: 'Erro ao carregar dados',
        });
      });
  };

  useEffect(() => {
    fetchData();
  }, []);

  const totalInEquitiesPercentage = data.totalInEquitiesPercentage || 0;
  const idealInEquitiesPercentage = data.idealInEquitiesPercentage || 0;
  const totalMatchesIdeal = totalInEquitiesPercentage === idealInEquitiesPercentage;

  const selectedEquityPositions = useMemo(() => {
    return data.equityPositions.filter((position) => selectedIds.includes(position.id));
  }, [selectedIds, data.equityPositions]);

  const positionsToBuy = useMemo(() => {
    // Usa `balancing` (delta em R$), não `balancingQuantity` (em ações): balancing não depende de
    // market_price, então um ativo recomendado sem preço sincronizado ainda aparece na lista.
    return selectedEquityPositions.filter((position) => Number(position.balancing) > 0);
  }, [selectedEquityPositions]);

  return (
    <div className="flex flex-col gap-5">
      <div className="bg-white rounded-custom20 p-4 h-full flex flex-col gap-5">
        <Title count={data.equityPositionsCount} />

        <div className="grid grid-cols-3 gap-5">
          <Info
            title="Total em Renda Variável"
            value={data.totalInEquities || 0}
            percentage={totalInEquitiesPercentage}
            circleClassName={totalMatchesIdeal ? 'bg-blueWhisper text-tealBase' : 'bg-redExtraLight2 text-redBase'}
            tooltip="Valores calculados com base no patrimônio total excluindo a previdência."
          />

          <Info
            title="Ideal em Renda Variável"
            value={data.idealInEquities || 0}
            percentage={idealInEquitiesPercentage}
            circleClassName="bg-blueWhisper text-tealBase"
            tooltip="Valores calculados com base no patrimônio total excluindo a previdência."
          />

          <Info
            title="Renda Variável em Trânsito"
            value={data.pendingSettlements || 0}
            icon={<RefreshCw size={32} strokeWidth={1.5} />}
            circleClassName="bg-white text-blueHighlight"
          />
        </div>

        <div className="flex flex-col gap-4">
          {data.kinds.map((kind) => {
            return (
              <EquityAccordion
                key={kind.kind}
                title={kind.tKindPluralized}
                investedPercentage={kind.percentage}
                positions={data.equityPositions.filter((p) => p.product.kind === kind.kind)}
                selectedIds={selectedIds}
                onToggle={toggleOne}
                onToggleAll={toggleGroup}
              />
            );
          })}
        </div>
      </div>

      {selectedEquityPositions.length > 0 && (
        <SalesPlanning
          equityPositions={selectedEquityPositions.filter((position) => position.amount > 0)}
          accountNumber={accountNumber}
          clientName={clientName}
        />
      )}

      {positionsToBuy.length > 0 && (
        <PurchasePlanning equityPositions={positionsToBuy} accountNumber={accountNumber} clientName={clientName} />
      )}
    </div>
  );
};

export default Equities;
